/* eslint-disable @typescript-eslint/no-explicit-any */
declare let google: any
declare let window: any
import { useRef, useCallback } from 'react'
import { useScript } from 'usehooks-ts'
import type { Status } from 'usehooks-ts'
import { useEffect, useState } from 'react'
import log from 'loglevel'
import { ReceptorID } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { useGetReceptor } from 'src/pages/Receptors/receptors.hooks'
import { useModalState } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import { useGoogleDriveContext } from '../../context/GoogleDriveContext'
import {
  PickerConfiguration,
  PickerResponse,
  GOOGLE_PICKER_SUPPORTED_FILE_TYPES,
} from './GoogleDriveWizard.constants'

export const useLoadGoogleApi = (shouldLoad = true): boolean => {
  const status: Status = useScript('https://apis.google.com/js/api.js')
  const [ready, setReady] = useState<boolean>(false)

  useEffect(() => {
    const onApiLoad = () => {
      setReady(true)
    }
    const loadApis = () => {
      window.gapi.load('client:auth2', () => null)
      window.gapi.load('picker', onApiLoad)
    }
    if (status === 'ready' && !ready && shouldLoad) {
      loadApis()
    }
  }, [status, ready, shouldLoad])

  return ready
}

export const useGooglePicker = (): ((
  config: PickerConfiguration,
  receptor?: any,
) => Promise<PickerResponse>) => {
  const { setFilesSelected } = useGoogleDriveContext()
  const showPicker = useRef<boolean>(false)
  const ready = useLoadGoogleApi(!showPicker.current)
  const { openModal } = useModalState()
  const getReceptor = useGetReceptor()

  const createPicker = useCallback(
    async (config: PickerConfiguration, receptor?: any) => {
      const callback = async (tokendata: {
        action: string
        docs: google.picker.DocumentObject[]
      }) => {
        if (tokendata.action === google.picker.Action.PICKED) {
          setFilesSelected?.(tokendata.docs)
          openModal(config.modalId)
        } else if (tokendata.action === google.picker.Action.CANCEL) {
          setFilesSelected?.([])
        }
        showPicker.current = false
      }
      const activeLinker = receptor
      if (
        !activeLinker?.credentials?.length &&
        !activeLinker?.getCredentials?.()?.length
      ) {
        openModal(ModalFormId.CONNECT_LINKER)
        return {
          isShown: false,
          invalidCredentials: false,
          exceptions: '',
        }
      }

      const exceptions =
        activeLinker?.exceptions || activeLinker?.getExceptions?.()
      if (exceptions?.length > 0) {
        return {
          isShown: false,
          invalidCredentials: false,
          exceptions: exceptions,
        }
      }

      const iscredvalid =
        activeLinker?.iscredvalid !== undefined
          ? activeLinker?.iscredvalid
          : activeLinker?.getIscredvalid?.()
      if (!iscredvalid) {
        return {
          isShown: false,
          invalidCredentials: true,
          exceptions: '',
        }
      }

      let token = ''
      try {
        const credentialsStr =
          activeLinker?.credentials || activeLinker?.getCredentials?.()
        const creds = JSON.parse(credentialsStr || '{}')
        token = creds.token?.access_token || ''

        const receptorId = activeLinker.id || activeLinker.getId?.()
        if (receptorId) {
          const freshReceptor = await getReceptor(
            new ReceptorID().setOid(receptorId),
          )
          const freshCredsStr = freshReceptor?.getCredentials() || '{}'
          const freshCreds = JSON.parse(freshCredsStr)
          if (freshCreds?.token?.access_token) {
            token = freshCreds.token.access_token
          }
        }
      } catch (e) {
        log.error('Failed to parse or fetch Google Drive token', e)
      }

      const view = new google.picker.DocsView(config.viewId)
        .setIncludeFolders(true)
        .setMimeTypes(
          config.mimeTypes
            ? config.mimeTypes
            : GOOGLE_PICKER_SUPPORTED_FILE_TYPES,
        )
        .setSelectFolderEnabled(config.selectFolderEnabled || false)

      const driveView = new google.picker.DocsView(config.viewId)
        .setIncludeFolders(true)
        .setMimeTypes(
          config.mimeTypes
            ? config.mimeTypes
            : GOOGLE_PICKER_SUPPORTED_FILE_TYPES,
        )
        .setSelectFolderEnabled(config.selectFolderEnabled || false)
        .setEnableDrives(true)

      const picker = new google.picker.PickerBuilder()
        .setOAuthToken(token)
        .setDeveloperKey(process.env.REACT_APP_GOOGLE_API_KEY || '')
        .setLocale('en')
        .addView(view)
        .addView(driveView)
        .setOrigin(window.location.origin)
        .setTitle('Select a file from Google Drive or Shared Drives')
        .setCallback(callback)
      config.selectableMimeTypes &&
        picker.setSelectableMimeTypes(config.selectableMimeTypes)
      config.allowMultiselect &&
        picker.enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      const pickerBuilder = picker.build()
      pickerBuilder.setVisible(true)

      return {
        isShown: true,
        invalidCredentials: false,
        exceptions: '',
      }
    },
    [openModal, setFilesSelected, getReceptor],
  )

  const openPicker = useCallback(
    async (config: PickerConfiguration, receptor?: any) => {
      if (ready && !showPicker.current) {
        showPicker.current = true
        return createPicker(config, receptor)
      }
      return {
        isShown: false,
        invalidCredentials: false,
        exceptions: '',
      }
    },
    [ready, createPicker],
  )

  return openPicker
}
